import { useEffect } from "react";
import dayjs from "dayjs";
import {
  Button,
  ConfigProvider,
  Flex,
  Form,
  Input,
  InputNumber,
  theme,
  Radio,
} from "antd";

import StyledModal from "./style";

import {
  DEFAULT_RETIREMENT_AGE,
  NEW_CLIENT_DEFAULT_DATA,
} from "../../../../../../utils/constant";

import {
  cleanCurrencyValue,
  getCurrencyValue,
  selectValue,
} from "../../../../../../utils/helper/general";

import { ReactComponent as IconOpenClientView } from "../../image/icon_open_client_view.svg";

const ModalClientDetails = ({
  clientData,
  loading,
  onCancel,
  onClickProposalOpen,
  onFinish,
  open,
  showOptionsClientType,
}) => {
  const { token } = theme.useToken();
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open || !form) return;

    if (!clientData) {
      form.resetFields();
      form.setFieldsValue(NEW_CLIENT_DEFAULT_DATA);
      return;
    }

    const guideInitialData = clientData.valueMap?.guideInitialData;
    const leadInitial = clientData.valueMap?.leadInitial;

    form.setFieldsValue({
      assets: !isNaN(+(leadInitial?.assets ?? guideInitialData?.assets))
        ? (leadInitial?.assets ?? guideInitialData?.assets)
        : 0,
      email: clientData.email,
      firstName: clientData.firstName,
      lastName: clientData.lastName,
      oldEmail: clientData.email,
      retirementAge:
        leadInitial?.retirementAge ??
        guideInitialData?.retirementAge ??
        DEFAULT_RETIREMENT_AGE,
      // in the DB can be saved zero
      birthYear:
        clientData.birthYear ||
        clientData.yearBorn ||
        guideInitialData?.yearBorn,
    });
  }, [open]);

  const onClickDone = () => {
    form.setFieldValue("startPlaning", false);
    form.submit();
  };

  const onClickStartPlanning = () => {
    form.setFieldValue("startPlaning", true);
    form.submit();
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Radio: {
            colorPrimary: token.radio_button_color,
          },
        },
      }}
    >
      <StyledModal
        className="add-client-modal"
        closeIcon={false}
        footer={
          <Flex justify="space-between">
            <Button htmlType="button" onClick={onCancel} shape="round">
              Cancel
            </Button>

            {clientData ? (
              <Button
                disabled={loading}
                form="clientInfoForm"
                htmlType="submit"
                shape="round"
                type="primary"
              >
                Save Changes
              </Button>
            ) : (
              <Flex gap={24}>
                <Button disabled={loading} onClick={onClickDone} shape="round">
                  Done
                </Button>

                <Button
                  disabled={loading}
                  onClick={onClickStartPlanning}
                  shape="round"
                  type="primary"
                >
                  Start Planning
                </Button>
              </Flex>
            )}
          </Flex>
        }
        onCancel={onCancel}
        open={open}
        title={
          <Flex align="center" justify="space-between">
            <b style={{ fontSize: 18 }}>
              {clientData ? "Edit" : "Add"} Lead or Client
            </b>
            {clientData && (
              <Button
                onClick={() => onClickProposalOpen(clientData.email)}
                shape="round"
                size="small"
                style={{ background: "#E6F4EF", color: "#1B5568" }}
                type="primary"
              >
                <Flex align="center" gap={4}>
                  <span>Open Advisor View</span>
                  <IconOpenClientView />
                </Flex>
              </Button>
            )}
          </Flex>
        }
        width={647}
      >
        <Form
          form={form}
          id="clientInfoForm"
          layout="vertical"
          onFinish={onFinish}
          size="large"
        >
          <Flex gap={16}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[
                {
                  message: "Enter new user First name, please.",
                  required: true,
                },
              ]}
              style={{ width: "100%" }}
            >
              <Input onClick={selectValue} />
            </Form.Item>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[
                {
                  message: "Enter new user Last name, please.",
                  required: true,
                },
              ]}
              style={{ width: "100%" }}
            >
              <Input onClick={selectValue} />
            </Form.Item>
          </Flex>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              {
                message: "Invalid email address, please re-enter.",
                required: true,
                type: "email",
              },
            ]}
          >
            <Input onClick={selectValue} />
          </Form.Item>

          <Flex gap={16}>
            <Form.Item
              label="Birth Year"
              name="birthYear"
              rules={[
                {
                  max: dayjs().format("YYYY") - 5,
                  min: 1940,
                  required: true,
                  type: "number",
                },
              ]}
              style={{ width: "100%" }}
            >
              <InputNumber
                id="yearBornInput"
                inputMode="numeric"
                onClick={selectValue}
                pattern="[0-9]*"
                placeholder="YYYY"
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              label="Retirement Age"
              name="retirementAge"
              rules={[
                {
                  type: "number",
                  min: 20,
                  max: 100,
                },
              ]}
              style={{ width: "100%" }}
            >
              <InputNumber
                inputMode="numeric"
                onClick={selectValue}
                pattern="[0-9]*"
                placeholder="YYYY"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Flex>

          <Form.Item
            label="Initial Investable Assets"
            name="assets"
            style={{ width: "100%" }}
          >
            <InputNumber
              formatter={getCurrencyValue}
              max={10000000}
              min={0}
              onClick={selectValue}
              parser={cleanCurrencyValue}
              step={1000}
              style={{ width: "100%" }}
            />
          </Form.Item>

          {showOptionsClientType && (
            <Form.Item
              name="clientType"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Radio.Group>
                <Flex gap={16} vertical>
                  <Radio value="lead">Lead</Radio>
                  <Radio value="client">Client</Radio>
                </Flex>
              </Radio.Group>
            </Form.Item>
          )}

          <Form.Item hidden name="oldEmail" />
          <Form.Item hidden name="startPlaning" />
        </Form>
      </StyledModal>
    </ConfigProvider>
  );
};

export default ModalClientDetails;
